import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControlLabel, Checkbox, Alert, CircularProgress,
  Collapse, FormControl, InputLabel, Select, MenuItem, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useAuth } from '../contexts/AuthContext';

interface InferenceModelClass {
  id: string;
  classIndex: number;
  className: string;
}

interface InferenceModel {
  id: string;
  name: string;
  displayName: string;
  backend: string;
  modelPath: string;
  task: string;
  isActive: boolean;
  dateCreated: string;
  trainedFromProjectId: number | null;
  lastTrainedAt: string | null;
  classes: InferenceModelClass[];
}

const EMPTY_FORM = {
  name: '',
  displayName: '',
  backend: 'libreyolo',
  modelPath: '',
  task: 'detect',
  isActive: true,
};

function ModelRow({ model, onEdit, onDelete }: {
  model: InferenceModel;
  onEdit: (m: InferenceModel) => void;
  onDelete: (m: InferenceModel) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' }, opacity: model.isActive ? 1 : 0.5 }}>
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small" onClick={() => setOpen((v) => !v)}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{model.name}</Typography></TableCell>
        <TableCell>{model.displayName}</TableCell>
        <TableCell>
          <Chip label={model.backend} size="small" variant="outlined" />
          {model.task === 'npr' && <Chip label="NPR" size="small" color="primary" variant="outlined" sx={{ ml: 0.5 }} />}
          {model.task === 'container' && <Chip label="Containers" size="small" color="primary" variant="outlined" sx={{ ml: 0.5 }} />}
        </TableCell>
        <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{model.modelPath}</Typography></TableCell>
        <TableCell>{model.classes.length}</TableCell>
        <TableCell>
          <Chip
            label={model.isActive ? 'Active' : 'Inactive'}
            color={model.isActive ? 'success' : 'default'}
            size="small"
          />
        </TableCell>
        <TableCell>
          {model.lastTrainedAt
            ? new Date(model.lastTrainedAt).toLocaleDateString()
            : <Typography variant="caption" color="text.secondary">—</Typography>}
        </TableCell>
        <TableCell align="right">
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(model)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDelete(model)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1, pl: 7 }}>
              {model.classes.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No classes defined.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {model.classes.map((c) => (
                    <Chip key={c.id} label={`${c.classIndex}: ${c.className}`} size="small" variant="outlined" />
                  ))}
                </Box>
              )}
              {model.trainedFromProjectId && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Trained from annotator project #{model.trainedFromProjectId}
                  {model.lastTrainedAt ? ` · ${new Date(model.lastTrainedAt).toLocaleString()}` : ''}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function InferenceModels() {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;

  const [models, setModels] = useState<InferenceModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Edit/Create dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InferenceModel | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState<InferenceModel | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => { load(); }, [showInactive]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/inference-models?includeInactive=${showInactive}`);
      if (!res.ok) throw new Error(`${res.status}`);
      setModels(await res.json());
    } catch (e: any) {
      setError('Failed to load inference models.');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setSaveError('');
    setDialogOpen(true);
  }

  function openEdit(m: InferenceModel) {
    setEditing(m);
    setForm({ name: m.name, displayName: m.displayName, backend: m.backend, modelPath: m.modelPath, task: m.task, isActive: m.isActive });
    setSaveError('');
    setDialogOpen(true);
  }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    try {
      let res: Response;
      if (editing) {
        res = await fetch(`/api/inference-models/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify(form),
        });
      } else {
        res = await fetch('/api/inference-models', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ ...form, classes: [] }),
        });
      }
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `${res.status}`);
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setSaveError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  function openDelete(m: InferenceModel) {
    setDeleteError('');
    setDeleteTarget(m);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await fetch(`/api/inference-models/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.status === 409) {
        setDeleteError(await res.text());
        return;
      }
      if (!res.ok) throw new Error(`${res.status}`);

      const result = await res.json().catch(() => null);
      setDeleteTarget(null);
      if (result && !result.filesDeleted) {
        setError(`Model deleted, but its files could not be removed automatically (${result.filesError || 'inference service unreachable'}). They'll need cleaning up manually.`);
      }
      await load();
    } catch (e: any) {
      setDeleteError('Failed to delete model.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Inference Models</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label={<Typography variant="body2">Show inactive</Typography>}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Model</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Name</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell>Backend</TableCell>
                <TableCell>Model Path</TableCell>
                <TableCell>Classes</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Trained</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {models.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No models found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                models.map((m) => (
                  <ModelRow key={m.id} model={m} onEdit={openEdit} onDelete={openDelete} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Inference Model' : 'Add Inference Model'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {saveError && <Alert severity="error">{saveError}</Alert>}
            <TextField
              label="Name (key)"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              fullWidth
              size="small"
              helperText="Unique machine-readable key, e.g. fox_cat"
            />
            <TextField
              label="Display Name"
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              required
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Backend</InputLabel>
              <Select label="Backend" value={form.backend} onChange={(e) => setForm((f) => ({ ...f, backend: e.target.value }))}>
                <MenuItem value="libreyolo">libreyolo</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Task</InputLabel>
              <Select label="Task" value={form.task} onChange={(e) => setForm((f) => ({ ...f, task: e.target.value }))}>
                <MenuItem value="detect">Object detection</MenuItem>
                <MenuItem value="npr">Number plate reading (NPR)</MenuItem>
                <MenuItem value="container">Container number and dock state</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Model Path"
              value={form.modelPath}
              onChange={(e) => setForm((f) => ({ ...f, modelPath: e.target.value }))}
              required
              fullWidth
              size="small"
              helperText="Relative to inference container, e.g. models/libreyolo/fox_cat/best.pt"
              sx={{ '& input': { fontFamily: 'monospace', fontSize: 13 } }}
            />
            {editing && (
              <FormControlLabel
                control={<Checkbox checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />}
                label="Active"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !form.name.trim() || !form.displayName.trim() || !form.modelPath.trim()}
          >
            {saving ? 'Saving…' : editing ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Model</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography>
            Permanently delete <strong>{deleteTarget?.displayName}</strong> and its model files? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
